/**
 * AI 빌더 서버 세션 확보 — 채팅과 같은 빌드 화면의 다른 AI 도구(생성기)가 함께 쓴다.
 * - 세션이 없으면 만들되, 같은 세대 안에 진행 중인 생성이 있으면 그 요청 하나를 함께 기다린다(중복 생성 방지).
 * - 버리는 기준은 대화 세대다 — 대화를 새로 시작하면(startNewSessionEpoch) 그 전에 시작한 생성 결과는 저장하지 않는다.
 *   채팅 턴 취소(run id)와는 무관하다. 턴의 낡은 응답 처리는 호출부(채팅 Provider)가 맡는다.
 */
import { useCallback, useRef } from 'react';

import { AI_BUILDER_CHAT_PLACEHOLDER } from '../consts';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';

import { isAiBuilderUsageBlocked } from './aiBuilderUsagePolicy';
import { type AiBuilderChatState } from './types';

export type EnsureServerSessionOptions = {
  /** 패널을 열며 확보할 때 — 소진 상태로 세션이 열리면 소진 계측을 남기고 draft 를 비운다. */
  trackBlockedOnOpen?: boolean;
};

export const useAiBuilderSharedSession = ({
  draftInstanceId,
  getChatEnvironment,
  getChatState,
  hasPendingRestore,
  updateState,
}: {
  draftInstanceId: string;
  getChatEnvironment: () => { locale: string; userCountryCode: string };
  getChatState: () => AiBuilderChatState;
  hasPendingRestore: () => boolean;
  updateState: (
    updater: (prev: AiBuilderChatState) => AiBuilderChatState,
  ) => void;
}) => {
  /**
   * 세션 세대 — 대화를 새로 시작할 때만 올라간다. 진행 중이던 세션 생성이 끝났을 때 세대가 바뀌었으면 그 세션은
   * 지난 대화 것이라 저장하지 않는다. 채팅 턴 취소(run id)와는 별개다 — 턴을 취소해도 만든 세션은 그대로 쓴다.
   */
  const sessionEpochRef = useRef(0);
  /** 진행 중인 세션 생성 — 같은 세대 안의 호출(채팅·생성기 사전 등록·실행)은 이 요청 하나를 기다린다. */
  const inFlightSessionRef = useRef<{
    epoch: number;
    promise: Promise<string | null>;
    trackBlockedOnOpen: boolean;
  } | null>(null);

  const createSharedSession = useCallback(
    async (entry: {
      epoch: number;
      trackBlockedOnOpen: boolean;
    }): Promise<string | null> => {
      const chatEnvironment = getChatEnvironment();
      const session = await aiBuilderChatAdapter.createSession({
        locale: chatEnvironment.locale,
        countryCode: chatEnvironment.userCountryCode,
        draftInstanceId,
      });
      // 그 사이 대화를 새로 시작했다 — 지난 대화의 세션은 저장하지 않는다.
      if (sessionEpochRef.current !== entry.epoch) return null;

      const currentSessionId = getChatState().sessionId;
      if (currentSessionId) return currentSessionId;

      const usageBlockedOnOpen =
        entry.trackBlockedOnOpen &&
        getChatState().panelOpen &&
        isAiBuilderUsageBlocked(session.usage);

      updateState(prev => {
        if (prev.sessionId) return prev;

        const nextState = {
          ...prev,
          sessionId: session.sessionId,
          usage: session.usage,
        };

        // 소진 상태로 세션이 열리면 소진 UI가 안내를 담당하므로 draft만 비운다.
        if (usageBlockedOnOpen && prev.panelOpen) {
          return {
            ...nextState,
            draft: '',
            selectedFile: null,
            placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default,
          };
        }

        return nextState;
      });

      if (usageBlockedOnOpen) {
        trackAiBuilderUsageBlocked(session.usage, 'open');
      }

      return session.sessionId;
    },
    [draftInstanceId, getChatEnvironment, getChatState, updateState],
  );

  /**
   * 서버 세션을 확보한다 — 있으면 그대로, 없으면 만들되 진행 중인 생성이 있으면 그 요청을 함께 기다린다.
   * 채팅 턴과 무관하다(생성기 포트가 쓴다). 채팅 턴의 낡은 응답 처리는 ensureServerSession 이 맡는다.
   */
  const ensureSharedSession = useCallback(
    (options: EnsureServerSessionOptions = {}): Promise<string | null> => {
      const existingSessionId = getChatState().sessionId;
      if (existingSessionId) return Promise.resolve(existingSessionId);

      // 복원 대기 중에 새 세션을 만들면 스냅샷의 session_id 를 못 쓰게 된다.
      // 가드는 유효한 스냅샷이 실제로 있을 때만 걸리므로 평시 경로엔 영향이 없다.
      if (hasPendingRestore()) return Promise.resolve(null);

      const epoch = sessionEpochRef.current;
      const inFlight = inFlightSessionRef.current;
      if (inFlight && inFlight.epoch === epoch) {
        // 늦게 합류한 패널 열기도 소진 계측을 놓치지 않게 요청을 남긴다.
        if (options.trackBlockedOnOpen) inFlight.trackBlockedOnOpen = true;
        return inFlight.promise;
      }

      const entry = {
        epoch,
        trackBlockedOnOpen: Boolean(options.trackBlockedOnOpen),
        promise: Promise.resolve<string | null>(null),
      };
      entry.promise = createSharedSession(entry).finally(() => {
        if (inFlightSessionRef.current === entry) {
          inFlightSessionRef.current = null;
        }
      });
      inFlightSessionRef.current = entry;
      return entry.promise;
    },
    [createSharedSession, getChatState, hasPendingRestore],
  );

  /** 새 대화 — 진행 중이던 세션 생성이 끝나도 이 대화에 저장하지 않는다. */
  const startNewSessionEpoch = useCallback(() => {
    sessionEpochRef.current += 1;
    inFlightSessionRef.current = null;
  }, []);

  return { ensureSharedSession, startNewSessionEpoch };
};
